/**
 * @class
 * @example
 * {
 *     viewClass        : FooView,                             // Just like `childView`, the view constructor
 *     deferredModules  : ['foo'],                             // If you need to initialize any deferred modules before showing the corresponding view
 *     label            : i18n.t('i18n.fooViewLabel'),  // The `label` to show for the view (if ommitted we will look for `headerTitle` within the view's prototype)
 *     name             : 'foo',                               // If you would like specify your own name to reference the view (otherwise the `viewClass` will be used in camelcase form)
 *     showActions      : ['module:foo:bar'],                  // If you want other actions to trigger the showing of the corresponding view
 *     uniqueShowAction : 'module:foo:route',                  // If you want to decide what happens when a tab is clicked (`appView:[view.cid]:show` will be registered and used)
 *     viewOptions      : {...},                               // Anything that should be passed to the view at creation
 * }
 */
import Base from '@netx/core/lib/netx-model';

import {getEnabledActions} from '@netx/core-permissions/properties/actions';
import {
	FindLegacyAction,
	RegisterLegacyAction,
} from '@netx/core/legacyActionManager';

class AppView extends Base {
	/** @type {boolean} */
	get isAppView() {
		return true;
	}

	/** @type {ES6Model} */
	get viewModel() {
		const view = this.get('view');
		if (view) {
			return view.model;
		}
		const viewOptions = this.get('viewOptions') || {};
		return viewOptions.model;
	}

	/**
	 * @property {object}                          defaults                         - Defaults
	 * @property {string}                          defaults.id                      - Unique `id` - the class name of the view if not provided
	 * @property {module:core/lib/netx-view}       defaults.viewClass               - View constructor
	 * @property {boolean}                         defaults.active                  - Is the view `model` currently active
	 * @property {array}                           defaults.deferredModules         - Deferred modules that must be loaded before the view can be `shown`/`rendered`
	 * @property {array}                           defaults.showAction              - An array of `actions` the view will listen for and show itself on
	 * @property {string}                          defaults.label                   - If you want set a sepcific `label` for this view (will be used by tabs and crumbs)
	 * @property {string}                          defaults.name                    - If you want set a sepcific `name` for this view (will be used by tabs)
	 * @property {boolean}                         defaults.queued                  - Is the view `queued` (this should not be set manually)
	 * @property {boolean}                         defaults.switched                - Is the view `switched` out (this should not be set manually)
	 * @property {string}                          defaults.uniqueShowAction        - This will be used as the primary unique `action` for showing the view. If not set, a unique one will be created for it
	 * @property {object}                          defaults.viewOptions             - The `options` that will be passed to the view at creation
	 * @property {module:core/lib/netx-ollection}  defaults.viewOptions.collection  - The `collection` for the view
	 * @property {module:core/lib/netx-model}      defaults.viewOptions.model       - The `model` for the view
	 */
	get defaults() {
		return {
			// id               : "",
			viewClass: null,
			active: false,
			deferredModules: undefined,
			showActions: [],
			label: undefined,
			name: undefined,
			queued: false,
			switched: false,
			uniqueShowAction: undefined,
			viewOptions: {
				collection: undefined,
				model: undefined,
			},
		};
	}

	/**
	 * @override
	 * @param {object} attrs Model attributes
	 * @param {object} opts Model options
	 * @fires {@link Netx.Models.AppView#destroy}
	 * @fires {@link Netx.Models.AppView#switchedBack}
	 * @listens {@link Netx.Models.AppView#remove}
	 * @listens {@link Netx.Models.AppView#change:view}
	 * @listens {@link Netx.Models.AppView#change:viewOptions:model}
	 * @listens {@link Netx.Models.AppView#change:viewOptions:collection}
	 */
	constructor(attrs = {}, opts = {}) {
		if (attrs.id == null) {
			const viewClass = attrs.viewClass;
			attrs.id = viewClass.__name__;
		}

		super(attrs, opts);

		// If a name was not set - add one
		!this.has('name') &&
			this.set('name', _.camelCaseToDash(_.toCamelCase(this.id)));

		// Hey, listen!
		// We deal with the nested object `viewOptions` in the set method.
		// Backbone will only tell you about the change, to a key, in an object,
		// on a model, once (at least until that model has `synced	since the last chnage).
		// But regardless of syncing (which we do not do) that still would not get us
		// deep change event namespacing

		// If a unique show action was not provided, make one. This ensures
		// that the simple action of showing a view does not require accidentally
		// firing off a conflicting event name. Also, you do not absolutely
		// have to define any `showActions` if you do not need anything special
		// and are really just using a viewCollection to create a tab interface.
		let uniqueShowAction = this.get('uniqueShowAction');
		if (this.get('uniqueShowAction') == null) {
			uniqueShowAction = `appView:${this.cid}:show`;
			this.set('uniqueShowAction', uniqueShowAction);
			RegisterLegacyAction([
				{
					event: uniqueShowAction,
					label: i18n.t('i18n.show'),
				},
			]);
		}
		// Check that the action provided has been registered, if not throw warning
		else {
			const action = FindLegacyAction(uniqueShowAction);
			if (!action) {
				console.warn(
					'The action `' +
						this.get('uniqueShowAction') +
						'` provided for multiView childView has not been registered. This will result in an error in `performAction`.',
				);
			}
		}

		// Hey, listen!
		this
			////////////////////////////////
			// MODEL
			////////////////////////////////
			// Update model
			.listenTo(this, 'change:viewOptions:model', (model) => {
				if (model !== this || !this.get('view')) return;
				// Since we are piping below
				const _model = this.get('viewOptions').model;
				_model != null && this.get('view').setModel(_model);
			})
			////////////////////////////////
			// !MODEL
			////////////////////////////////

			////////////////////////////////
			// COLLECTION
			////////////////////////////////
			// Update collection
			.listenTo(this, 'change:viewOptions:collection', (model) => {
				if (model !== this || !this.get('view')) return;
				// Since we are piping below
				const collection = this.get('viewOptions').collection;
				collection != null && this.get('view').setCollection(collection);
			})
			////////////////////////////////
			// !COLLECTION
			////////////////////////////////

			// Cleanup
			.listenTo(this, 'remove', (model, collection, options = {}) => {
				// Using `options`, spelled out, to not conflict with the `opts` passed to `initialize`
				// In the current artchitecture it would not make much sense to remove a model entirely from a viewCollection and add it to another
				// We can offer an option to pop the view from one place to the next but in the end always returns home and can only live in 1 other
				// `viewCollection` at a time:
				// app -> fullScreen || modal -> app || app -> modal
				// And even:
				// app -> fullScreen -> modal || app -> modal -> fullScreen
				// And by just adding the same instance of the model you do not loose any data inputed in the view. If we were trully removed from
				// our original `viewCollection` then we fire off a destroy event which in turn cleans it up from other `viewCollections` it may be in.
				if (model !== this) return;

				if (this.collection === collection) {
					// TODO: remove any created uniqueShowActions - but first discuss if simply removing
					// an action from the Action array is enough to be considered "cleaned up"
					this.stopListening();
					// Let it known, for all intents and purposes, we were destroyed
					// There is no coming back from this kind of removal
					this.trigger('destroy', this, this.collection);
				}
				// We were in another `viewCollection` and our `viewCollection` is not the collection we were removed from
				else if (
					this.currentCollection &&
					this.currentCollection._listenId === collection._listenId
				) {
					// When removed from a `viewCollection`, check to see if we should be switching back to our original `viewCollection`
					// Switch back - the switch isn't really anything more than an event - the other collections this may still be in will
					// be listening for this event to remove us from there as well
					this.trigger('switchedBack', this, this.collection);
					// Be sure to delete this so that we only fire `switchedBack` on the first removal from not our original `viewCollection`
					delete this.currentCollection;
				}
			})

			////////////////////////////////
			// EVENT PIPING
			////////////////////////////////
			// Pipe view events - only once
			// If the view is removed so is the model and vice versa
			.listenToOnce(this, 'change:view', (model) => {
				if (model !== this) return;

				// The view gets set in Netx.View.prototype.addChildView
				const view = this.get('view');

				// Pipe them all - if this turns out to be too costly we can get specific
				// to events that we know we want
				this.listenTo(view, 'all', (...args) => {
					// Make `arguments` an array
					args = [...args];
					const length = args.length;
					// We are not consistent in sending the view with the event trigger
					// Like: this.trigger('rendered'); -> this.trigger('rendered', this);
					// Because really we have never needed to pipe anything and always knew
					// if we were explicitly listening to a view's events within another
					// view - this abstracts it so any view which has access to this model's
					// collection can listen for all events that occur on the child views
					// within.
					// Prepend event with "view:"
					args[0] = `view:${args[0]}`;
					if (length === 1 || (length > 1 && args[1] !== view)) {
						args.splice(1, 0, view);
					}
					this.trigger.apply(this, args);
				});
			});
		////////////////////////////////
		// !EVENT PIPING
		////////////////////////////////

		// A very slight pause before being considered "synced"
		_.defer(() => {
			this.trigger('sync', this);
		});
	}
	/**
	 * We only care about `viewOptions`
	 * @override
	 * @param {object|string} key Either the key or an object of key values
	 * @param {mixed} value Either a value or, if the key is an object, it is the options object
	 * @param {object} opts If key is not an object this the options object, otherwise `null`
	 */
	set(key, value, opts) {
		let newViewAttrs;

		const changes = [];
		const attrsToRemove = {};
		const attrsToChange = {};

		let attrs = {};
		let _opts;
		// If the key is an object then we have only two args and the second one is the opts
		if (_.isObject(key)) {
			_opts = value || {};
			attrs = key;
		} else {
			attrs[key] = value;
		}

		// Updating `viewOptions`
		if (attrs.viewOptions != null) {
			// Protect `viewOptions` as an object
			if (!_.isObject(attrs.viewOptions)) {
				attrs.viewOptions = {};
			}
			newViewAttrs = attrs.viewOptions;
		}

		// Deal with changes to `viewOptions`
		if (newViewAttrs && this.attributes.viewOptions) {
			// Clone a copy of old options
			const oldViewAttrs = _.clone(this.attributes.viewOptions);
			if (newViewAttrs) {
				// Loop over new values
				_.each(newViewAttrs, (attr, k) => {
					if (_.isUndefined(oldViewAttrs[k])) {
						// Add to changes
						changes.push({key: k, change: 'added'});
						attrsToChange[k] = attr;
					}
					// _.isEqual does not work for comparing models/collections
					else if (oldViewAttrs[k] !== attr) {
						// Add to changes
						changes.push({key: k, change: 'changed'});
						attrsToChange[k] = attr;
					}
				});
				// Loop over old values
				_.each(oldViewAttrs, (attr, k) => {
					if (newViewAttrs[k] == null) {
						// Add to changes
						changes.push({key: k, change: 'removed'});
						attrsToRemove[k] = attr;
					}
				});
			}
		}

		// We want default backbone behavior here - see override in netx.js
		// This is because we do not care about all the stuff set being tracked in subCollections/subModels
		// We know that when this model is removed it just stops listening - if you are removing this model
		// from a viewCollection to put somewhere else - you would be [currently] doing it wrong
		const ret = Backbone.Model.prototype.set.call(this, attrs, _opts);

		// Loop over changes and emit named change events
		if (changes.length) {
			// Unless silent was passed in opts
			if (_opts?.silent !== true) {
				// Loop and emit
				_.each(changes, (change) => {
					// The arguments are the same as any other Backbone.Model change event
					this.trigger(
						'change:viewOptions:' + change.key,
						this,
						newViewAttrs[change.key],
						_opts,
					);
				});
				// Update view's options (except model/collection that is a special case and handled in the
				// listeners of this initialize)
				const view = this.get('view');
				if (view) {
					// Set any changed attrs on the view
					_.extend(view, _.omit(attrsToChange, 'model', 'collection'));
					// Remove any values not present in the newViewAttrs from the view
					// save the model and/or collection
					_.each(_.omit(attrsToRemove, 'model', 'collection'), (opt, k) => {
						delete view[k];
					});
				}
			}
		}

		// Backbone.Model.protoype.set return
		return ret;
	}

	/**
	 * Is the provided `viewCollection` our original `viewCollection`?
	 * @method
	 * @param {Netx.Collections.AppViews} collection Our `viewCollecton`
	 * @return {boolean}
	 */
	isMyOriginalCollection(collection) {
		return !this.collection ||
			!collection ||
			(this.get('queued') &&
				this.collection.queueFor &&
				this.collection.queueFor._listenId === collection._listenId) ||
			this.collection._listenId === collection._listenId
			? true
			: false;
	}
	/**
	 * @method
	 * @param {object|string} key Either the key or an object of key values
	 * @param {mixed} value Either a value or, if the key is an object, it is the options object
	 * @param {object} opts If key is not an object this the options object, otherwise `null`
	 */
	setViewOption(key, value, opts) {
		const newViewOptions = _.clone(this.get('viewOptions') || {});
		if (_.isObject(key)) {
			_.extend(newViewOptions, key);
		} else {
			newViewOptions[key] = value;
		}
		return this.set('viewOptions', newViewOptions, opts);
	}
	/**
	 * Switch back to original `viewCollection`
	 * @method
	 */
	switchBack() {
		this.currentCollection && this.currentCollection.remove(this);
		return this;
	}
	/**
	 * Switch to another `viewCollection`
	 * @method
	 * @param {Netx.Collections.AppViews} collection Our `viewCollecton`
	 */
	switchTo(collection) {
		this.collection.switchTo(this, collection);
		return this;
	}

	/**
	 * @method
	 * @return {boolean} Valid
	 */
	isViewActionValid() {
		const view = this.get('view');
		if (!view) return true;

		const uniqueShowAction = this.get('uniqueShowAction');
		const actions = getEnabledActions(
			[FindLegacyAction(uniqueShowAction)],
			view,
		);

		return actions.length > 0;
	}

	/** @type {boolean} */
	static get __isAppView__() {
		return true;
	}

	/** @type {string} */
	static get __name__() {
		return 'AppView';
	}
}

export default AppView;
